import { Button, Grid, Stack, Tab, Tabs, Typography } from "@mui/material";
import { Box } from "@mui/system";
import BankingRow from "src/components/banking/BankingRow";
import HighlightedInformation from "src/components/banking/HighlightedInformation";
import { BankingBreakdownStyles } from "src/components/banking/styles/BankingBreakdown.styles";
import { BANK_DATA } from "./BankingData";
import LoadingComponent from "../application/LoadingComponent";
import api from "src/store/features/api";
import {
  useHasExistingStripeAccountsQuery,
  useLazyCreateStripeVbankTransferQuery,
} from "src/store/features/api/generated";
import { useState, useEffect, useRef } from "react";
import StripePaymentPage from "./StripePaymentPage";
import { getSessionItem } from "src/utils/SessionManagement";
import usePermission from "src/utils/hooks/usePermission";

type Country = "AUD" | "USD" | "USBurling" | "USSubsidiary" | "NZD" | "GBP" | "EUR";

interface IntBankingProps {
  copyToClipboard: (el: any) => void;
  country: Country;
  countryCode: string;
  subsidiary: string;
  clientDetails: {
    customerId: string;
    name: string;
  };
}

// Helper component for rendering a list of banking details
const BankDetailsList = ({
  details,
  copyToClipboard,
  clientDetails,
}: {
  details: any[];
  copyToClipboard: (el: any) => void;
  clientDetails: { customerId: string; name: string };
}) => {
  if (!details) return null;
  return (
    <>
      {details.map((el, it) => (
        <Box key={`${it}+${el.placeHolder}`}>
          <BankingRow
            placeholder={el.placeHolder}
            content={el.content}
            description={el.description}
            isCopy={el.hasIcon}
            copyToClipboard={copyToClipboard}
            clientDetails={clientDetails}
          />
        </Box>
      ))}
    </>
  );
};

// Helper component for rendering Stripe tab content
const StripeTabContent = ({
  isCheckingExisting,
  hasExistingAccounts,
  stripeData,
  isLoading,
  error,
  hasValidData,
  onGenerate,
  clientDetails,
}: {
  isCheckingExisting: boolean;
  hasExistingAccounts: boolean | undefined;
  stripeData: any;
  isLoading: boolean;
  error: any;
  hasValidData: boolean | undefined;
  onGenerate: () => void;
  clientDetails: { customerId: string; name: string };
}) => {
  if (isCheckingExisting) {
    return <LoadingComponent message="Checking for existing Stripe accounts" />;
  }

  const showLoadingText = !stripeData && !isLoading && !error;

  return (
    <>
      {showLoadingText && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {hasExistingAccounts
              ? "Loading your existing Stripe bank transfer details..."
              : "Click the button below to generate your Stripe bank transfer details"}
          </Typography>
          {!hasExistingAccounts && (
            <Button variant="contained" color="primary" onClick={onGenerate} disabled={isLoading}>
              Generate Bank Transfer Details
            </Button>
          )}
        </Box>
      )}

      {isLoading && <LoadingComponent message="Loading Stripe bank transfer details" />}

      {error && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="error" variant="body1" sx={{ mb: 2 }}>
            Failed to load bank transfer details. Please try again.
          </Typography>
          <Button variant="contained" color="primary" onClick={onGenerate} disabled={isLoading}>
            Retry
          </Button>
        </Box>
      )}

      {hasValidData && <StripePaymentPage stripeData={stripeData} clientDetails={clientDetails} />}
    </>
  );
};

function IntBanking(props: IntBankingProps) {
  // props
  const { copyToClipboard, country, clientDetails, countryCode, subsidiary } = props;

  const { isGuest } = usePermission();

  // styles
  const classes = BankingBreakdownStyles();

  // redux
  const { data: bankDetails, isLoading } = api.useGetBurlingBankDetailsQuery();
  const { data: hasExistingStripeAccounts, isLoading: isHasExistingStripeAccountsLoading } =
    useHasExistingStripeAccountsQuery(undefined, { skip: isGuest || subsidiary !== "US" });
  const [
    createStripeVbankTransfer,
    { data: stripeData, isLoading: stripeLoading, error: stripeError },
  ] = useLazyCreateStripeVbankTransferQuery();

  const stripeFeature = getSessionItem("features")?.includes("stripe-vban");
  const [activeTab, setActiveTab] = useState<number>(0);
  const hasAutoLoadedStripeRef = useRef(false);

  // Only treat as valid bank details when response has the expected data
  const hasValidStripeData =
    stripeData &&
    typeof stripeData === "object" &&
    "aba_financial_address" in stripeData &&
    stripeData.aba_financial_address != null;

  // Hide Domestic Wire tab when we got a response but Stripe data is unavailable (e.g. stripeEnabled: false)
  const showDomesticWireTab =
    stripeFeature && (hasValidStripeData || stripeData == null || stripeLoading || !!stripeError);

  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleGenerateStripeDetails = () => {
    createStripeVbankTransfer();
  };

  // Automatically load Stripe data if user has existing accounts (only once per mount)
  useEffect(() => {
    if (hasAutoLoadedStripeRef.current) return;
    const shouldAutoLoad =
      hasExistingStripeAccounts && !stripeLoading && !stripeError && stripeData == null;
    if (shouldAutoLoad) {
      hasAutoLoadedStripeRef.current = true;
      createStripeVbankTransfer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- createStripeVbankTransfer omitted; call guarded by ref
  }, [hasExistingStripeAccounts, stripeData, stripeLoading, stripeError]);

  const Wrapper = ({ children }: { children: any }) => {
    return (
      <Grid container className={classes.container}>
        <Stack spacing={3} sx={{ width: "100%", marginBottom: "1rem" }}>
          <HighlightedInformation
            currency={country}
            countryCode={countryCode}
            showTransferTimes={true}
            showDescription={false}
          />
          <Box mt={2}>
            <Typography className={classes.title}>
              Please send funds to the following account
            </Typography>
          </Box>
          {children}
        </Stack>
      </Grid>
    );
  };

  if (isLoading)
    return (
      <Wrapper>
        <LoadingComponent message="Loading banking details" />
      </Wrapper>
    );

  // Subsidiary priority logic
  if (subsidiary === "US")
    return (
      <>
        <Grid container className={classes.container}>
          <Stack spacing={3} sx={{ width: "100%", marginBottom: "1rem" }}>
            <HighlightedInformation
              currency={"USD"}
              countryCode={"US"}
              showTransferTimes={true}
              showDescription={false}
            />
          </Stack>
        </Grid>
        <Box mt={2}>
          <Typography className={classes.title}>
            Please send funds to the following account
          </Typography>
        </Box>
        <Tabs value={showDomesticWireTab ? activeTab : 0} onChange={handleTabChange}>
          {showDomesticWireTab && <Tab label="Domestic Wire" />}
          <Tab label="US Bank Details" />
        </Tabs>

        {activeTab === 0 && showDomesticWireTab && (
          <Box sx={{ width: "100%" }}>
            <StripeTabContent
              isCheckingExisting={isHasExistingStripeAccountsLoading}
              hasExistingAccounts={hasExistingStripeAccounts}
              stripeData={stripeData}
              isLoading={stripeLoading}
              error={stripeError}
              hasValidData={hasValidStripeData}
              onGenerate={handleGenerateStripeDetails}
              clientDetails={clientDetails}
            />
          </Box>
        )}

        {(activeTab === 1 || !showDomesticWireTab) && (
          <BankDetailsList
            details={BANK_DATA["USSubsidiary"]}
            copyToClipboard={copyToClipboard}
            clientDetails={clientDetails}
          />
        )}
      </>
    );

  const bankDataList =
    country === "USD" && bankDetails?.burlingBank ? BANK_DATA["USBurling"] : BANK_DATA[country];

  return (
    <Wrapper>
      <BankDetailsList
        details={bankDataList}
        copyToClipboard={copyToClipboard}
        clientDetails={clientDetails}
      />
    </Wrapper>
  );
}

export default IntBanking;
